<script lang="ts" setup>
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';
import { i18n } from '@/i18n';
import { computed, ref, watch } from 'vue';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import { DISPLAY_INVENTORY_YEAR_MONTH_MAX_RANGE } from '../constants';
import { YearMonthRange } from '@/coreDomainsModule/entities/yearMonthRange';

// props
const props = defineProps<{
  defaultStartYearMonth: IYearMonth;
  defaultEndYearMonth: IYearMonth;
  modalState: boolean;
}>();

// emits
const emits = defineEmits<{
  (e: 'on-next', selectYearMonthInfo: YearMonthRange): void;
  (e: 'on-back'): void;
}>();

//変数
const t = i18n.t;
const defaultYearMonth = DateService.convertDateToIYearMonth(new Date());
const locale = computed(() => i18n.locale);
const startYearMonth = ref<IYearMonth>(defaultYearMonth);
const endYearMonth = ref<IYearMonth>(defaultYearMonth);
const modalState = ref<boolean>(false);

// propsの値を初期値として設定
// モーダルの表示状態
watch(
  () => props.modalState,
  () => {
    modalState.value = props.modalState;
  },
  { immediate: true } // 初期値を即座に反映
);

// 開始年月の初期値
watch(
  () => props.defaultStartYearMonth,
  () => {
    startYearMonth.value = props.defaultStartYearMonth;
  },
  { immediate: true } // 初期値を即座に反映
);

// 終了年月の初期値
watch(
  () => props.defaultEndYearMonth,
  () => {
    endYearMonth.value = props.defaultEndYearMonth;
  },
  { immediate: true } // 初期値を即座に反映
);

const isDisabledApplyButton = computed(() => {
  return (
    startYearMonth.value.year === 0 || endYearMonth.value.year === 0 || startYearMonth.value.month === 0 || endYearMonth.value.month === 0
  );
});

/**
 * 内示遍歴ダウンロードモーダルの開始年月を選択した際の処理
 * @param selectedYearMonth - 選択された年月
 */
const onSelectedStartYearMonth = async (selectedYearMonth: IYearMonth) => {
  if (isDisabledYearMonth(selectedYearMonth)) {
    // 無効な範囲を選択した場合、エラートーストを表示
    toast.warning(t('10年前から2年後までの範囲で選択してください'));
    return;
  } else if (isStartYearMonthAfterEndYearMonth(selectedYearMonth)) {
    // 開始年月が終了年月よりも後の場合、エラートーストを表示
    toast.warning(t('開始年月は終了年月よりも前にしてください'));
    return;
  } else if (!isSelectedStartYearMonthValidRange(selectedYearMonth)) {
    // 開始年月が終了年月との範囲が24ヶ月以内でない場合、エラートーストを表示
    toast.warning(t('開始年月は終了年月から24ヶ月以内で選択してください'));
    return;
  }

  startYearMonth.value = selectedYearMonth;
};

/**
 * 内示遍歴ダウンロードモーダルの終了年月を選択した際の処理
 * @param selectedYearMonth - 選択された年月
 */
const onSelectedEndYearMonth = async (selectedYearMonth: IYearMonth) => {
  if (isDisabledYearMonth(selectedYearMonth)) {
    // 無効な範囲を選択した場合、エラートーストを表示
    toast.warning(t('10年前から2年後までの範囲で選択してください'));
    return;
  } else if (isEndYearMonthBeforeStartYearMonth(selectedYearMonth)) {
    // 終了年月が開始年月よりも前の場合、エラートーストを表示
    toast.warning(t('終了年月は開始年月よりも後にしてください'));
    return;
  } else if (!isSelectedEndYearMonthValidRange(selectedYearMonth)) {
    // 終了年月が開始年月との範囲が24ヶ月以内でない場合、エラートーストを表示
    toast.warning(t('終了年月は開始年月から24ヶ月以内で選択してください'));
    return;
  }

  endYearMonth.value = selectedYearMonth;
};

/**
 * 対象年月が無効かどうかを取得
 * @param selectedYearMonth - 選択された開始年月
 */
const isDisabledYearMonth = (selectedYearMonth: IYearMonth): boolean => {
  // 有効範囲は(当月の120ヶ月前)以上、(当月の24ヶ月先)以下
  const currentYearMonth = DateService.convertDateToIYearMonth(new Date());
  const { updateYear: _headYear, updateMonth: _headMonth } = DateService.updateYearMonth(
    currentYearMonth.year,
    currentYearMonth.month,
    -120
  );
  const headYearMonth = { year: _headYear, month: _headMonth };
  const { updateYear: _tailYear, updateMonth: _tailMonth } = DateService.updateYearMonth(currentYearMonth.year, currentYearMonth.month, 24);
  const tailYearMonth = { year: _tailYear, month: _tailMonth };
  const betweenYearMonths = DateService.getYearMonthsBetween(headYearMonth, tailYearMonth);
  // 有効範囲外の場合は無効かつ、終了年月よりも後の年月を選択している場合は無効
  return !betweenYearMonths.some((yearMonth) => yearMonth.year === selectedYearMonth.year && yearMonth.month === selectedYearMonth.month);
};

/**
 * 開始年月が終了年月よりも後かどうかを取得
 * @param selectedYearMonth - 選択された開始年月
 */
const isStartYearMonthAfterEndYearMonth = (selectedYearMonth: IYearMonth): boolean => {
  // 開始年月が選択されていない場合は無効
  if (endYearMonth.value.year === 0 || endYearMonth.value.month === 0) {
    return true;
  }

  // 終了年月が開始年月より前の場合は無効
  const selectedTotal = selectedYearMonth.year * 12 + selectedYearMonth.month;
  const endTotal = endYearMonth.value.year * 12 + endYearMonth.value.month;
  return endTotal - selectedTotal < 0;
};

/**
 * 終了年月が開始年月よりも前かどうかを判定
 * @param selectedYearMonth - 選択された開始年月
 */
const isEndYearMonthBeforeStartYearMonth = (selectedYearMonth: IYearMonth): boolean => {
  // 開始年月が選択されていない場合は無効
  if (startYearMonth.value.year === 0 || startYearMonth.value.month === 0) {
    return true;
  }

  // 終了年月が開始年月より前の場合は無効
  const selectedTotal = selectedYearMonth.year * 12 + selectedYearMonth.month;
  const startTotal = startYearMonth.value.year * 12 + startYearMonth.value.month;
  return selectedTotal - startTotal < 0;
};

/**
 * 開始年月選択時のの有効範囲をチェックする
 * @param selectedYearMonth - 選択された開始年月
 */
const isSelectedStartYearMonthValidRange = (selectedYearMonth: IYearMonth): boolean => {
  // 有効範囲は開始と終了の範囲が24ヶ月以内
  const selectedTotal = selectedYearMonth.year * 12 + selectedYearMonth.month;
  const endTotal = endYearMonth.value.year * 12 + endYearMonth.value.month;
  return Math.abs(endTotal - selectedTotal) <= DISPLAY_INVENTORY_YEAR_MONTH_MAX_RANGE;
};

/**
 * 終了年月選択時のの有効範囲をチェックする
 * @param selectedYearMonth - 選択された終了年月
 */
const isSelectedEndYearMonthValidRange = (selectedYearMonth: IYearMonth): boolean => {
  // 有効範囲は開始と終了の範囲が24ヶ月以内
  const selectedTotal = selectedYearMonth.year * 12 + selectedYearMonth.month;
  const startTotal = startYearMonth.value.year * 12 + startYearMonth.value.month;
  return Math.abs(selectedTotal - startTotal) <= DISPLAY_INVENTORY_YEAR_MONTH_MAX_RANGE;
};
</script>

<template>
  <InfoModal
    :modal-state="modalState"
    :back-button-label="t('キャンセル')"
    :next-button-label="t('決定')"
    :is-disabled-next-button="isDisabledApplyButton"
    @on-back="emits('on-back')"
    @on-next="emits('on-next', { startYearMonth: startYearMonth, endYearMonth: endYearMonth })"
  >
    <template #title>{{ t('表示範囲設定') }}</template>
    <template #description>
      <div>{{ t('下記から表示期間を選択してください。') }}</div>
    </template>

    <div class="download-timeline-modal-content__main-content main-content">
      <MonthPicker
        class="main-content__calendar download-timeline-calendar"
        :model-value="startYearMonth"
        :locale="locale"
        :prop-format="t('MonthPickerFormat')"
        @update:model-value="onSelectedStartYearMonth($event)"
      />
      {{ t('から') }}
      <MonthPicker
        class="main-content__calendar download-timeline-calendar"
        :model-value="endYearMonth"
        :locale="locale"
        :prop-format="t('MonthPickerFormat')"
        @update:model-value="onSelectedEndYearMonth($event)"
      />
      {{ t('まで') }}
    </div>
  </InfoModal>
</template>

<style lang="scss" scoped>
.download-timeline-modal-content {
  height: 100%;
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 24px;
}

.main-content {
  background-color: $backgroundDefault;
  border-radius: 8px;
  display: grid;
  grid-template-columns: repeat(4, auto);
  gap: 8px;
  align-items: center;
  font-size: $fontSizeS;
  font-weight: 400;
  padding: 16px;

  &__calendar {
    min-width: 141px;
    max-width: 141px;
    height: 36px;
    background-color: $semanticWhite;
    border-radius: 4px;
    border: 1px solid $borderDefault;
    display: grid;
    place-items: center;
  }
}
</style>

<style lang="scss">
.download-timeline-calendar input {
  font-family: Noto Sans JP;
  font-size: $fontSizeM;
  font-weight: 700;
  color: $textDefault;
}
</style>
